import { useEffect, useState } from "react";

import { useVerificationEngine } from "../hooks/useVerificationEngine";
import { useVerificationSocket } from "../hooks/useVerificationSocket";
import { useVerificationStatusPolling } from "../hooks/useVerificationStatusPolling";
import { VerificationService } from "../services/verificationService";
import type {
  KycLevel,
  KycStatus,
  VerificationPurpose,
  VerificationStatusResponse,
} from "../types/verification-api.types";
import type { VerificationResult } from "../types/verification.types";
import { CaptureStep } from "./CaptureStep";
import { ConsentGate } from "./ConsentGate";
import { ProcessingScreen } from "./ProcessingScreen";
import { ResultScreen } from "./ResultScreen";
import { StepIntroCard } from "./StepIntroCard";
import { VerificationStepper } from "./VerificationStepper";

export interface EmbeddedVerificationFlowProps {
  /** Existing identity to verify (marco C — the identity is already created). */
  identityId: string;
  purpose: VerificationPurpose;
  /** Fired when the engine reaches a terminal result, so the host can refresh its status. */
  onResult?: (result: VerificationResult) => void;
  /**
   * Dismisses the host surface once the verification is approved. The terminal action of a dialog is to
   * close, not to navigate — and without one the only way out is the X in the corner, which reads as an
   * escape hatch rather than as "you are done". Omitted, the approved screen simply shows no action.
   */
  onDone?: () => void;
}

/**
 * Marco C — the engine embedded in a `ColocaDialog`/`ColocaDrawer` for an EXISTING identity (no create step).
 * Uses the authenticated (JWT) capture/status endpoints keyed on `identityId`. Renders without the outer
 * card/full-screen shell, since the dialog already provides the surface.
 */
export function EmbeddedVerificationFlow({
  identityId,
  purpose,
  onResult,
  onDone,
}: EmbeddedVerificationFlowProps) {
  const engine = useVerificationEngine({ purpose, frame: "embedded" });
  const { goTo, applyStatus } = engine;
  const [lastStatus, setLastStatus] = useState<VerificationStatusResponse | null>(null);

  // Pull current status once; jump straight to the result/processing if it is already past PENDING.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await VerificationService.getStatus(identityId);
        if (cancelled) return;
        setLastStatus(data);
        if (data.status !== "PENDING") applyStatus(data.status);
      } catch {
        // No status yet — start from the intro.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [identityId, applyStatus]);

  const pollingEnabled = engine.screen === "processing" || engine.result === "review";
  useVerificationStatusPolling(
    async () => (await VerificationService.getStatus(identityId)).data,
    pollingEnabled,
    (data) => {
      setLastStatus(data);
      applyStatus(data.status);
    },
  );

  // Preferred channel: real-time push (the polling above stays as fallback).
  useVerificationSocket(identityId, (payload) => {
    const status = payload.status as KycStatus;
    setLastStatus({
      status,
      level: (payload.level ?? "NN") as KycLevel,
      rejection_reason: payload.rejection_reason,
    });
    applyStatus(status);
  });

  // Surface terminal results to the host (e.g. to refresh the status pill).
  useEffect(() => {
    if (engine.screen === "result" && engine.result) onResult?.(engine.result);
  }, [engine.screen, engine.result, onResult]);

  const startCapture = () => VerificationService.startCapture(identityId).then((r) => r.data);
  const retryTarget = purpose === "KYC" ? "capture-doc" : "capture";

  const renderScreen = () => {
    switch (engine.screen) {
      case "intro":
        return <StepIntroCard purpose={purpose} onStart={() => goTo("consent")} />;
      case "consent":
        return <ConsentGate purpose={purpose} onAccept={() => goTo(retryTarget)} />;
      case "capture-doc":
        // Single KYC capture: the embedded SDK runs document + selfie/liveness in one session.
        return <CaptureStep start={startCapture} mode="document" onComplete={() => goTo("processing")} />;
      case "capture":
        return <CaptureStep start={startCapture} mode="company" onComplete={() => goTo("processing")} />;
      case "processing":
        return <ProcessingScreen purpose={purpose} />;
      case "result": {
        const done = engine.result === "verified" ? onDone : undefined;
        return (
          <ResultScreen
            result={engine.result ?? "review"}
            rejectionReason={lastStatus?.rejection_reason}
            onPrimary={engine.result === "rejected" ? () => goTo(retryTarget) : done}
            primaryLabelKey={done ? "result_verified_cta_done" : undefined}
          />
        );
      }
      default:
        return <ProcessingScreen purpose={purpose} />;
    }
  };

  return (
    <div className="space-y-6">
      {engine.screen === "intro" ? null : (
        <VerificationStepper purpose={purpose} currentStep={engine.step} totalSteps={engine.totalSteps} />
      )}
      {renderScreen()}
    </div>
  );
}
